import {
  BitkitButton,
  BitkitNoteCard,
  IconChevronDown,
  IconChevronUp,
  IconSparkleFilled,
  rem,
} from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Collapsible } from '@chakra-ui/react/collapsible';
import { useToken } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { ReactNode } from 'react';

type BitriseAiCardProps = Omit<BoxProps, 'children'> & {
  title?: string;
  subtitle: string;
  pendingText?: string;
  errorTitle?: string;
  errorSubtitle?: string;
  children?: ReactNode;
  isPending?: boolean;
  isError?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onRetry?: () => void;
  footer?: ReactNode;
};

const BitriseAiCard = ({
  title = 'Bitrise AI',
  subtitle,
  pendingText = 'AI summary in progress...',
  errorTitle = 'Summary interrupted',
  errorSubtitle = 'Bitrise AI encountered an unexpected error.',
  children,
  isPending,
  isError,
  isOpen = false,
  onToggle,
  onRetry,
  footer,
  ...boxProps
}: BitriseAiCardProps) => {
  // `{colors.…}` references to slash-named tokens are parsed as color-mix syntax in compound values, so resolve them here.
  const [surfaceColor, aiBorderColor] = useToken('colors', ['background/primary', 'status/ai/border']);

  if (isPending) {
    return <BitkitNoteCard {...boxProps} role="progressbar" status="progress" message={pendingText} />;
  }

  if (isError) {
    return (
      <Box
        {...boxProps}
        display="flex"
        border="1px solid"
        borderColor="border/regular"
        borderRadius="8"
        overflow="hidden"
      >
        <Box background="background/secondary" paddingX="8" paddingY="12">
          <IconSparkleFilled color="icon/secondary" size="24" />
        </Box>
        <Box
          flex="1"
          paddingX="12"
          paddingY="12"
          background="background/primary"
          display="flex"
          alignItems="center"
          gap="24"
        >
          <Box flex="1" display="flex" flexDirection="column" gap="4">
            <Text textStyle="body/lg/semibold" color="text/secondary">
              {errorTitle}
            </Text>
            <Text textStyle="body/md/regular" color="text/tertiary">
              {errorSubtitle}
            </Text>
          </Box>
          {onRetry && (
            <BitkitButton variant="tertiary" size="sm" onClick={onRetry}>
              Try again
            </BitkitButton>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      {...boxProps}
      background={`linear-gradient(${surfaceColor}) padding-box, ${aiBorderColor} border-box`}
      border="1px solid transparent"
      borderRadius="8"
      display="flex"
      overflow="hidden"
    >
      <Box background="gradient/ai/background/minimal-vertical" borderLeftRadius={rem(7)} paddingX="8" paddingY="12">
        <IconSparkleFilled color="color/indigo/base" size="24" />
      </Box>
      <Box flex="1" display="flex" flexDirection="column" background="background/primary" overflow="hidden">
        <Box display="flex" alignItems="center" gap="8" paddingLeft="12" paddingRight="16" paddingY="12">
          <Box flex="1" display="flex" flexDirection="column" gap="4">
            <Text textStyle="body/lg/semibold" color="status/ai/text">
              {title}
            </Text>
            <Text textStyle="body/md/regular" color="text/body">
              {subtitle}
            </Text>
          </Box>
          {onToggle && (
            <BitkitButton
              variant="tertiary"
              size="sm"
              suffixIcon={isOpen ? IconChevronUp : IconChevronDown}
              onClick={onToggle}
            >
              {isOpen ? 'Hide details' : 'View details'}
            </BitkitButton>
          )}
        </Box>

        <Collapsible.Root open={isOpen}>
          <Collapsible.Content>{children}</Collapsible.Content>
        </Collapsible.Root>

        {footer && (
          <Box borderTop="1px solid" borderColor="border/minimal" paddingLeft="12" paddingRight="16" paddingY="12">
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BitriseAiCard;
